import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Group, Select, Skeleton, Stack, Text, Title, Alert, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconFileTypePdf } from '@tabler/icons-react'
import { api } from '@/api/client'
import { getWafDashboardPdf } from '@/api/waf'
import { formatApiError } from '@/api/errors'
import type { WafResource } from '@/api/waf'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
import { WafDashboardPanel } from './WafDashboardPanel'

interface ManagedWaf {
  id: number
  name: string
  world?: string
  primary_domain?: string | null
}

function listWafs(): Promise<{ items: ManagedWaf[] }> {
  return api.get<{ items: ManagedWaf[] }>('/v1/waf/managed').then((r) => r.data)
}

interface WafDetail {
  resources?: WafResource[]
}

function getWafDetail(id: number): Promise<WafDetail> {
  return api.get<WafDetail>(`/v1/waf/managed/${id}`).then((r) => r.data)
}

export function WafDashboardPage() {
  const [params, setParams] = useSearchParams()
  const [window, setWindow] = useState<'24h' | '7d' | '30d'>('7d')
  const wafs = useQuery({ queryKey: ['waf', 'managed'], queryFn: listWafs })
  const items = useMemo(() => wafs.data?.items ?? [], [wafs.data])

  const paramWaf = params.get('waf')
  const selectedId = useMemo(() => {
    const fromParam = paramWaf && items.some((w) => String(w.id) === paramWaf) ? Number(paramWaf) : null
    return fromParam ?? (items.length ? items[0].id : null)
  }, [paramWaf, items])

  const options = items.map((w) => ({
    value: String(w.id),
    label: w.primary_domain ? `${w.name} — ${w.primary_domain}` : w.name,
  }))

  // ── Filtro de domínio (host) ──────────────────────────────────────────────
  const [host, setHost] = useState<string | undefined>(undefined)

  // Ao trocar de WAF, zera o domínio (evita host de outro WAF).
  useEffect(() => {
    setHost(undefined)
  }, [selectedId])

  const detail = useQuery({
    queryKey: ['waf-detail', selectedId],
    queryFn: () => getWafDetail(selectedId as number),
    enabled: selectedId !== null,
  })

  const hosts = useMemo(
    () => Array.from(new Set((detail.data?.resources ?? []).flatMap((r) => r.domains ?? []))).sort(),
    [detail.data],
  )

  const domainOptions = useMemo(
    () => [{ value: '', label: 'Todos os domínios' }, ...hosts.map((h) => ({ value: h, label: h }))],
    [hosts],
  )

  // Download do PDF via axios (Bearer) — âncora nativa iria sem o header e daria 401.
  const [pdfLoading, setPdfLoading] = useState(false)
  async function handleExportPdf() {
    if (selectedId === null) return
    setPdfLoading(true)
    try {
      await getWafDashboardPdf(selectedId, window)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Exportar PDF',
        message: formatApiError(err, 'Falha ao gerar o PDF.'),
      })
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Dashboard do WAF</Title>
        <Group gap="sm" align="flex-end">
          <Select
            label="WAF"
            placeholder="Selecione um WAF"
            data={options}
            value={selectedId !== null ? String(selectedId) : null}
            onChange={(v) => setParams(v ? { waf: v } : {}, { replace: true })}
            searchable
            w={360}
            disabled={wafs.isLoading || items.length === 0}
          />
          <Select
            label="Domínio"
            data={domainOptions}
            value={host ?? ''}
            onChange={(v) => setHost(v ? v : undefined)}
            searchable
            w={260}
            disabled={selectedId === null || detail.isLoading || hosts.length === 0}
          />
          <Tooltip
            label={host ? 'O PDF exporta todos os domínios deste WAF.' : ''}
            disabled={!host}
            withArrow
          >
            <Button
              onClick={handleExportPdf}
              leftSection={<IconFileTypePdf size={16} />}
              variant="light"
              loading={pdfLoading}
              disabled={selectedId === null}
            >
              Exportar PDF
            </Button>
          </Tooltip>
        </Group>
      </Group>

      {/* Antes: `?? []` sem ramo de erro. Com a listagem fora do ar a tela dizia que nao ha WAF
        gerenciado e mandava rodar a descoberta — trabalho inutil, porque descobrir de novo nao
        conserta uma consulta que nao respondeu. */}
      <EstadoDeConsulta
        consulta={wafs}
        carregando={<Stack gap="md"><Skeleton h={120} /><Skeleton h={260} /></Stack>}
        vazio={items.length === 0}
        estadoVazio={
          /* A lista passou a ser ESCOPADA (TASK-10999), entao "vazio" tem duas causas: nao
            existe WAF cadastrado, ou nenhum esta no escopo de quem abriu. Mandar rodar a
            descoberta e' conselho errado no segundo caso — e a tela de descoberta pede outra
            permissao. O texto nao cita quantidade de proposito: dizer "existem N, nenhum seu"
            vazaria a existencia de WAF de outro tenant, que e' o que o escopo esconde. */
          <Alert color="gray">
            Nenhum WAF gerenciado no seu escopo. Confira o tenant selecionado no topo; se o WAF
            ainda não foi cadastrado, rode a descoberta na tela de WAF.
          </Alert>
        }
      >
        {selectedId === null ? (
          <Card withBorder padding="lg"><Text c="dimmed">Selecione um WAF para ver o dashboard.</Text></Card>
        ) : (
          <WafDashboardPanel wafId={selectedId} window={window} onWindowChange={setWindow} host={host} />
        )}
      </EstadoDeConsulta>
    </Stack>
  )
}
